import { useEffect, useState } from 'react';
import axios from 'axios';
import GoBack from 'components/actions/GoBack';
import { MdOutlineVpnKey, MdOutlineDescription, MdSmartToy, MdContentCopy, MdCheck } from 'react-icons/md';
import ApiEndpointPlayground from 'components/apiKeyScope/ApiEndpointPlayground';
import { CONFIG } from 'variables/config';

const API_KEY_STORAGE = 'leadrs-api-tester-key';
const MD_DOC_URL = `${window.location.origin}/bourse-du-lead-api.md`;
const INDUSTRIES_ENDPOINT_ANCHOR = 'endpoint-industries';
const REGIONS_ENDPOINT_ANCHOR = 'endpoint-regions';
// Same convention as formTemplates/edit.tsx's DISPLAY_API_URL: show the public
// api.leadrs.co domain rather than the raw Scalingo hostname.
const DISPLAY_API_URL =
    CONFIG.apiUrl.replace(/\/$/, '') === 'https://api-leadrs-co.osc-fr1.scalingo.io'
        ? 'https://api.leadrs.co'
        : CONFIG.apiUrl.replace(/\/$/, '');

const buildAgentInstructions = (apiKey: string) =>
    `Intègre l'API Bourse du Lead de Leadrs dans ce projet pour lister et acheter des leads en utilisant Leadrs comme fournisseur.
Documentation complète (Markdown) : ${MD_DOC_URL}
Authentification : en-tête HTTP "x-api-key" avec la valeur "${apiKey.trim() || '<votre-cle-api>'}"
Endpoints : GET /marketplace/leads (lister les leads disponibles), POST /marketplace/leads/:leadId/buy (acheter un lead, débite le solde du compte), GET /marketplace/wallet (consulter le solde du compte), GET /marketplace/purchases (lister les leads déjà achetés), GET /marketplace/industries (lister les industries et sous-industries, aucun scope requis) et GET /marketplace/regions (lister les régions/zones climatiques, aucun scope requis).
Respecte le scope accordé à cette clé (voir la documentation) et gère les réponses 403 "Missing scope" en conséquence.`;

const SCOPE_ROWS: { scope: string; description: string }[] = [
    { scope: '(aucun)', description: "Accordé par défaut à toute clé API : GET /marketplace/industries et GET /marketplace/regions ne nécessitent aucun scope." },
    { scope: 'view', description: 'Consulter les leads de la Bourse du Lead, toutes industries confondues.' },
    { scope: 'view:industry:*', description: 'Équivalent à "view", toutes industries.' },
    { scope: 'view:industry:<Industrie>', description: 'Consulter uniquement les leads de cette industrie (toutes sous-industries).' },
    { scope: 'view:industry:<Industrie>:<SousIndustrie>', description: 'Consulter uniquement les leads de cette sous-industrie précise.' },
    { scope: 'buy', description: 'Acheter un lead (débit du solde du compte), toutes industries confondues.' },
    { scope: 'buy:industry:<Industrie>', description: 'Acheter uniquement des leads de cette industrie.' },
    { scope: 'buy:industry:<Industrie>:<SousIndustrie>', description: 'Acheter uniquement des leads de cette sous-industrie précise.' },
];

const ApiDocs = () => {
    const [apiKey, setApiKey] = useState('');
    const [copiedInstructions, setCopiedInstructions] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    const [industryOptions, setIndustryOptions] = useState<string[]>([]);
    const [subIndustriesByIndustry, setSubIndustriesByIndustry] = useState<Record<string, string[]>>({});
    const [regionOptions, setRegionOptions] = useState<string[]>([]);

    useEffect(() => {
        const saved = localStorage.getItem(API_KEY_STORAGE);
        if (saved) setApiKey(saved);
    }, []);

    // Fills the searchable selects below straight from the two catalog endpoints
    // this page documents — via the buyer's own JWT session (always unrestricted,
    // no need for the pasted API key just to browse this page).
    useEffect(() => {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        axios
            .get(`${CONFIG.apiUrl}/marketplace/industries`, { headers })
            .then((res) => {
                const industries: { name: string; subIndustries: string[] }[] = res.data?.industries ?? [];
                setIndustryOptions(industries.map((i) => i.name));
                setSubIndustriesByIndustry(Object.fromEntries(industries.map((i) => [i.name, i.subIndustries])));
            })
            .catch(() => {});
        axios
            .get(`${CONFIG.apiUrl}/marketplace/regions`, { headers })
            .then((res) => setRegionOptions(res.data?.regions ?? []))
            .catch(() => {});
    }, []);

    const handleApiKeyChange = (value: string) => {
        setApiKey(value);
        if (value.trim()) localStorage.setItem(API_KEY_STORAGE, value.trim());
        else localStorage.removeItem(API_KEY_STORAGE);
    };

    const handleCopyInstructions = () => {
        navigator.clipboard.writeText(buildAgentInstructions(apiKey)).then(() => {
            setCopiedInstructions(true);
            setTimeout(() => setCopiedInstructions(false), 2000);
        });
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(MD_DOC_URL).then(() => {
            setCopiedLink(true);
            setTimeout(() => setCopiedLink(false), 2000);
        });
    };

    return (
        <>
            <div className="mb-3 flex">
                <GoBack />
            </div>
            <div className="w-full">
                <div className="rounded-xl border border-leadrs-brand bg-leadrs-text px-6 py-5">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white/10">
                            <MdSmartToy className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-geist text-base font-semibold text-white">
                                Vous vibe-codez ? Branchez votre agent directement sur cette API.
                            </h3>
                            <p className="mt-1 font-geist text-sm text-white/70">
                                Copiez ces instructions et le lien du fichier Markdown ci-dessous, collez-les dans votre
                                agent IA préféré (Cursor, Claude Code, Copilot...), et laissez-le intégrer Leadrs comme
                                fournisseur pour lister et acheter vos leads directement.
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <button
                                    onClick={handleCopyInstructions}
                                    className="flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 font-geist text-xs font-medium text-leadrs-text hover:opacity-90"
                                >
                                    {copiedInstructions ? <MdCheck className="h-4 w-4" /> : <MdContentCopy className="h-4 w-4" />}
                                    {copiedInstructions ? 'Instructions copiées' : "Copier les instructions pour l'agent"}
                                </button>
                                <button
                                    onClick={handleCopyLink}
                                    className="flex items-center gap-1.5 rounded-lg border border-white/30 px-3 py-2 font-geist text-xs font-medium text-white hover:bg-white/10"
                                >
                                    {copiedLink ? <MdCheck className="h-4 w-4" /> : <MdContentCopy className="h-4 w-4" />}
                                    {copiedLink ? 'Lien copié' : 'Copier le lien .md'}
                                </button>
                                <a
                                    href={MD_DOC_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-1.5 rounded-lg border border-white/30 px-3 py-2 font-geist text-xs font-medium text-white hover:bg-white/10"
                                >
                                    Ouvrir le .md
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-4 rounded-xl border border-leadrs-stroke bg-white px-6 py-6">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-leadrs-purple-soft">
                            <MdOutlineDescription className="h-5 w-5 text-leadrs-brand" />
                        </div>
                        <div>
                            <h2 className="font-crimson text-2xl font-semibold leading-tight text-leadrs-text">
                                Documentation API - Bourse du Lead
                            </h2>
                            <p className="mt-1 font-geist text-sm text-leadrs-text-soft">
                                Intégrez la Bourse du Lead depuis vos propres outils grâce à une clé API scopée. Créez et
                                gérez votre clé depuis Mon compte → Clé API.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-4 rounded-xl border border-leadrs-stroke bg-white px-6 py-5">
                    <h3 className="font-geist text-base font-semibold text-leadrs-text">Authentification</h3>
                    <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                        Chaque requête vers la Bourse du Lead doit inclure l'en-tête <code className="font-mono text-leadrs-brand">x-api-key</code> avec
                        votre clé API. Une requête sans le scope requis pour l'action demandée renvoie une erreur{' '}
                        <code className="font-mono">403</code> précisant exactement le scope manquant.
                    </p>
                    <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                        URL de base : <code className="font-mono text-leadrs-text">{DISPLAY_API_URL}</code>
                    </p>

                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-left font-geist text-sm">
                            <thead>
                                <tr className="border-b border-leadrs-stroke text-xs uppercase text-leadrs-text-soft">
                                    <th className="py-2 pr-4">Scope</th>
                                    <th className="py-2">Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {SCOPE_ROWS.map((row) => (
                                    <tr key={row.scope} className="border-b border-leadrs-stroke/60">
                                        <td className="py-2 pr-4 font-mono text-xs text-leadrs-brand">{row.scope}</td>
                                        <td className="py-2 text-leadrs-text-soft">{row.description}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-4 rounded-xl border border-leadrs-yellow-focus bg-leadrs-yellow-soft px-6 py-5">
                    <div className="flex items-center gap-2">
                        <MdOutlineVpnKey className="h-5 w-5 text-leadrs-yellow-focus" />
                        <h3 className="font-geist text-base font-semibold text-leadrs-text">Votre clé API</h3>
                    </div>
                    <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                        Collez votre clé ici pour qu'elle soit insérée automatiquement dans les exemples ci-dessous et
                        pour tester les requêtes en direct. Elle n'est stockée que dans votre navigateur.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <input
                            type="text"
                            value={apiKey}
                            onChange={(e) => handleApiKeyChange(e.target.value)}
                            placeholder="lrk_..."
                            className="min-w-[280px] flex-1 rounded-lg border border-leadrs-stroke px-3 py-2 font-mono text-sm text-leadrs-text"
                        />
                        {apiKey && (
                            <button
                                onClick={() => handleApiKeyChange('')}
                                className="rounded-lg border border-leadrs-stroke px-3 py-2 font-geist text-xs font-medium text-leadrs-text-soft hover:bg-white"
                            >
                                Oublier cette clé
                            </button>
                        )}
                    </div>
                </div>

                <div className="mt-4 flex flex-col gap-4">
                    <ApiEndpointPlayground
                        id={INDUSTRIES_ENDPOINT_ANCHOR}
                        method="GET"
                        title="Lister les industries et sous-industries"
                        description="Retourne le catalogue complet des industries et sous-industries de Leadrs, utile pour construire les filtres 'industry'/'subIndustry' des autres endpoints."
                        pathTemplate="/marketplace/industries"
                        requiredScope="aucun (accordé par défaut à toute clé API)"
                        apiKey={apiKey}
                    />

                    <ApiEndpointPlayground
                        id={REGIONS_ENDPOINT_ANCHOR}
                        method="GET"
                        title="Lister les régions et zones climatiques"
                        description="Retourne la liste complète des régions utilisables dans le filtre 'region' de GET /marketplace/leads : les 3 zones climatiques et tous les départements français."
                        pathTemplate="/marketplace/regions"
                        requiredScope="aucun (accordé par défaut à toute clé API)"
                        apiKey={apiKey}
                    />

                    <ApiEndpointPlayground
                        method="GET"
                        title="Lister les leads disponibles"
                        description="Parcourt les leads en vente sur la Bourse du Lead. Avec une clé API, le résultat est automatiquement restreint aux industries/sous-industries couvertes par le scope 'view' de la clé."
                        pathTemplate="/marketplace/leads"
                        queryParams={[
                            {
                                name: 'industry',
                                label: 'industry',
                                placeholder: 'ex: Assurance',
                                options: industryOptions,
                                sourceHint: { text: 'Valeurs fournies par GET /marketplace/industries', anchorId: INDUSTRIES_ENDPOINT_ANCHOR },
                            },
                            {
                                name: 'subIndustry',
                                label: 'subIndustry',
                                placeholder: 'ex: Auto',
                                options: (values) => subIndustriesByIndustry[values.industry] || [],
                                emptyMessage: "Choisissez d'abord une industrie",
                                sourceHint: { text: 'Valeurs fournies par GET /marketplace/industries', anchorId: INDUSTRIES_ENDPOINT_ANCHOR },
                            },
                            {
                                name: 'region',
                                label: 'region',
                                placeholder: 'ex: Zone Climatique H1, Paris (75)',
                                options: regionOptions,
                                sourceHint: { text: 'Valeurs fournies par GET /marketplace/regions', anchorId: REGIONS_ENDPOINT_ANCHOR },
                            },
                            { name: 'rgpd', label: 'rgpd', placeholder: 'true' },
                            { name: 'search', label: 'search', placeholder: '' },
                            { name: 'sort', label: 'sort', placeholder: 'newest | price_asc | price_desc' },
                            { name: 'page', label: 'page', placeholder: '1' },
                            { name: 'limit', label: 'limit', placeholder: '25' },
                        ]}
                        requiredScope="view (ou view:industry:*, view:industry:<Industrie>[:<SousIndustrie>])"
                        apiKey={apiKey}
                    />

                    <ApiEndpointPlayground
                        method="POST"
                        title="Acheter un lead"
                        description="Achète un lead précis en débitant le solde du compte acheteur. Le prix est toujours recalculé côté serveur, jamais transmis par le client."
                        pathTemplate="/marketplace/leads/:leadId/buy"
                        pathParams={[{ name: 'leadId', label: 'leadId', placeholder: 'ID du lead à acheter', required: true }]}
                        requiredScope="buy (ou buy:industry:*, buy:industry:<Industrie>[:<SousIndustrie>]), évalué sur l'industrie réelle du lead"
                        apiKey={apiKey}
                    />

                    <ApiEndpointPlayground
                        method="GET"
                        title="Consulter son solde"
                        description="Retourne le solde actuel du compte acheteur, utile pour vérifier la capacité d'achat avant d'appeler l'endpoint d'achat."
                        pathTemplate="/marketplace/wallet"
                        requiredScope="buy (n'importe quelle variante : buy, buy:industry:*, ou buy:industry:<Industrie>[:<SousIndustrie>], quelle que soit l'industrie)"
                        apiKey={apiKey}
                    />

                    <ApiEndpointPlayground
                        method="GET"
                        title="Lister les leads déjà achetés"
                        description="Retourne les leads achetés via la Bourse du Lead (achats via une offre/commande classique non inclus). Contrairement à la liste des leads disponibles, le résultat n'est pas restreint par industrie : ce sont des leads déjà possédés, pas de nouvel inventaire du marché."
                        pathTemplate="/marketplace/purchases"
                        queryParams={[
                            { name: 'page', label: 'page', placeholder: '1' },
                            { name: 'limit', label: 'limit', placeholder: '25' },
                        ]}
                        requiredScope="view (n'importe quelle variante, quelle que soit l'industrie)"
                        apiKey={apiKey}
                    />
                </div>
            </div>
        </>
    );
};

export default ApiDocs;
